// src/components/instructor/SeasonOverview.js
//
// Scheduling pilot (Pass 43) — Season Overview (item 3).
// Modern equivalent of Sam's season matrix: instructor × date, showing AM / PM /
// Night capacity DERIVED from exact startAt/endAt (precise times stay canonical).
// Answers "who is free / occupied in AM, PM, Night on a given date?" at a glance.
// Colour = payment state (or entry-type for internal events). Click a slot → detail.

import React, { useMemo, useState, useEffect } from 'react';
import {
  deriveSlots, PAYMENT_STATES, SCHEDULE_ENTRY_TYPES, INSTRUCTOR_CATEGORIES, DISCIPLINES, isClientBooking,
} from '../../utils/scheduleModel';
import { deriveActivityType, ACTIVITY_LABEL } from '../../utils/activityModel';
import { normalizePaymentState, PAYMENT, paymentLabel } from '../../utils/paymentSemantics';
import { ActivityPaymentLegend, activityColor, PAYMENT_COLOR } from './ops/opsShared';
import { zonedDateKey, zonedDateParts, zonedStartOfDay, wallClockToInstant, LEGACY_OPS_TIMEZONE } from '../../utils/opsTime';

const C = { card: '#FFFFFF', card2: '#F5F4F0', border: 'rgba(0,0,0,0.08)', t1: '#1C1B18', t2: '#57564F', t3: '#8A8980', blue: '#2563EB', free: '#EDEBE6' };
const font = "'DM Sans',system-ui,sans-serif";
const DAY_MS = 86400000;

const toDate = (v) => (!v ? null : typeof v?.toDate === 'function' ? v.toDate() : v?.seconds ? new Date(v.seconds * 1000) : v instanceof Date ? v : new Date(v));

// §8 colour for an occupied slot. In hosted mode a non-client event is coloured by its
// ACTIVITY (so Driving ≠ Concierge); a client booking keeps payment-state colour (payment is
// a second dimension for client lessons only). Legacy mode keeps the entry-type colour.
function slotColor(b, hosted) {
  if (!isClientBooking(b)) {
    if (hosted) return activityColor(deriveActivityType(b)).fg;
    return SCHEDULE_ENTRY_TYPES[b.scheduleEntryType]?.color || '#57564F';
  }
  // §10 hosted client payment tint via the canonical semantics (legacy 'unpaid' → not recorded).
  if (hosted) {
    const c = PAYMENT_COLOR[normalizePaymentState(b.paymentState ?? b.paymentStatus)];
    return c ? c.fg : C.blue;
  }
  const ps = PAYMENT_STATES[b.paymentState];
  return ps ? ps.color : C.blue;
}

export default function SeasonOverview({ bookings = [], instructors = [], onSelectBooking, initialStartDate, timeZone, hosted = false }) {
  // Pass 82C.2 §20 — every day key / column label / month label / start-of-day anchor is computed in the
  // ORGANIZATION's timezone (defaults to the legacy SAS zone so SAS output is visually identical to before).
  const tz = timeZone || LEGACY_OPS_TIMEZONE;
  const dKey = (d) => zonedDateKey(d, tz);
  const bKey = (v) => { const d = toDate(v); return d ? dKey(d) : ''; };
  const fmtCol = (d) => d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', timeZone: tz });
  const fmtMonth = (d) => d.toLocaleDateString('en-GB', { month: 'short', timeZone: tz });
  const startOf = (d) => { const inst = zonedStartOfDay(d, tz); return inst ? inst.getTime() : (() => { const t = new Date(); t.setHours(0, 0, 0, 0); return t.getTime(); })(); };
  const [startTs, setStartTs] = useState(() => (initialStartDate instanceof Date ? startOf(initialStartDate) : startOf(new Date())));

  // §2 reset the "From" window whenever the season's initialStartDate changes — so selecting
  // a historical season lands inside its real range, never a stale current-year month.
  useEffect(() => {
    if (initialStartDate instanceof Date) setStartTs(startOf(initialStartDate));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialStartDate && initialStartDate.getTime()]);
  const [days, setDays] = useState(10);
  const [fDiscipline, setFDiscipline] = useState('all');
  const [fCategory, setFCategory] = useState('all');
  const [fPayment, setFPayment] = useState('all');
  const [fInstructor, setFInstructor] = useState('all');

  const dates = useMemo(() => Array.from({ length: days }, (_, i) => new Date(startTs + i * DAY_MS)), [startTs, days]);

  // index: instructorId -> dateKey -> { am:[], pm:[], night:[] }
  const index = useMemo(() => {
    const idx = {};
    for (const b of bookings) {
      if (b.status === 'canceled') continue;
      if (fDiscipline !== 'all' && b.discipline !== fDiscipline && isClientBooking(b)) continue;
      // §10 hosted payment filter matches canonical normalized state; legacy matches raw.
      if (fPayment !== 'all' && isClientBooking(b)) {
        const pv = hosted ? normalizePaymentState(b.paymentState ?? b.paymentStatus) : b.paymentState;
        if (pv !== fPayment) continue;
      }
      const iid = b.instructorId || '__unassigned__';
      // Pass 46H: prefer the known serviceDate; historical unknown-time records have a
      // null startAt but a real serviceDate, so they still appear on the correct day.
      const k = b.serviceDate || bKey(b.startAt);
      if (!k) continue;
      const slots = deriveSlots(b, { timeZone: tz });
      idx[iid] = idx[iid] || {};
      idx[iid][k] = idx[iid][k] || { am: [], pm: [], night: [], untimed: [] };
      // Unknown/low-confidence clock time → shown on the date, but NEVER occupies AM/PM/Night.
      if (b.hasOperationalTime === false || (!slots.am && !slots.pm && !slots.night)) idx[iid][k].untimed.push(b);
      else {
        if (slots.am) idx[iid][k].am.push(b);
        if (slots.pm) idx[iid][k].pm.push(b);
        if (slots.night) idx[iid][k].night.push(b);
      }
    }
    return idx;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings, fDiscipline, fPayment, hosted, tz]);

  // Pass 46F: group by the canonical resolved category (school|collective|unclassified)
  // supplied by OpsSchedulePanel for the viewed season — never raw instructorCategory.
  const visibleInstructors = useMemo(() => instructors.filter((i) => {
    if (fInstructor !== 'all' && i.id !== fInstructor) return false;
    if (fCategory !== 'all' && (i.category || 'unclassified') !== fCategory) return false;
    return true;
  }), [instructors, fInstructor, fCategory]);

  const groups = [
    { key: 'school', label: INSTRUCTOR_CATEGORIES.sas_school.label, list: visibleInstructors.filter((i) => i.category === 'school') },
    { key: 'collective', label: INSTRUCTOR_CATEGORIES.sas_collective.label, list: visibleInstructors.filter((i) => i.category === 'collective') },
    // §10 commercial category not set ≠ roster review. In hosted (operations) mode these are
    // confirmed-current staff whose commercial category is simply unset.
    { key: 'unclassified', label: hosted ? 'Other staff · category not set' : 'Needs review / uncategorised', list: visibleInstructors.filter((i) => (i.category || 'unclassified') === 'unclassified') },
  ].filter((g) => g.list.length);

  const capBadge = (i) => {
    const parts = [];
    if (i.skiLevel != null && i.skiLevel !== '') parts.push(`Ski ${i.skiLevel}`);
    if (i.snowboardLevel != null && i.snowboardLevel !== '') parts.push(`SB ${i.snowboardLevel}`);
    return parts.join(' / ') || '—';
  };

  const Slot = ({ list, label }) => {
    const occupied = list.length > 0;
    const b = list[0];
    return (
      <div
        onClick={() => occupied && onSelectBooking && onSelectBooking(b)}
        title={occupied ? `${label}: ${isClientBooking(b) ? (b.serviceNameSnapshot || b.bookingType || 'Lesson') : (ACTIVITY_LABEL[deriveActivityType(b)] || 'Event')}${b.requesterName ? ` — ${b.requesterName}` : ''}` : `${label}: free`}
        style={{
          flex: 1, height: 15, borderRadius: 3, cursor: occupied ? 'pointer' : 'default',
          background: occupied ? slotColor(b, hosted) : C.free,
          border: occupied ? 'none' : `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontSize: 8, fontWeight: 700, position: 'relative',
        }}
      >
        {occupied ? label[0] : ''}
        {list.length > 1 && <span style={{ position: 'absolute', top: -5, right: -3, fontSize: 8, color: C.t2, background: '#fff', borderRadius: 6, padding: '0 2px', border: `1px solid ${C.border}` }}>{list.length}</span>}
      </div>
    );
  };

  return (
    <div style={{ fontFamily: font, color: C.t1 }}>
      {/* controls */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        <label style={{ fontSize: 12, color: C.t2 }}>From <input type="date" value={dKey(new Date(startTs))} onChange={(e) => { if (e.target.value) setStartTs(startOf(wallClockToInstant(e.target.value, '12:00', tz))); }} style={inp(140)} /></label>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} style={inp(110)}>{[7, 10, 14, 21].map((n) => <option key={n} value={n}>{n} days</option>)}</select>
        <select value={fCategory} onChange={(e) => setFCategory(e.target.value)} style={inp(170)}>
          <option value="all">All categories</option>
          <option value="school">SAS Snowsports School</option>
          <option value="collective">SAS Collective</option>
          {/* §3 unclassified COMMERCIAL category is "Category not set" in operations mode —
              roster review is a participation concept, not a category. */}
          <option value="unclassified">{hosted ? 'Category not set' : 'Needs review'}</option>
        </select>
        <select value={fDiscipline} onChange={(e) => setFDiscipline(e.target.value)} style={inp(150)}>
          <option value="all">All disciplines</option>
          {Object.entries(DISCIPLINES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={fPayment} onChange={(e) => setFPayment(e.target.value)} style={inp(180)}>
          <option value="all">All payment states</option>
          {/* §10 hosted uses the canonical payment vocabulary the tint/legend also use. */}
          {hosted
            ? Object.values(PAYMENT).map((k) => <option key={k} value={k}>{paymentLabel[k]}</option>)
            : Object.entries(PAYMENT_STATES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={fInstructor} onChange={(e) => setFInstructor(e.target.value)} style={inp(160)}>
          <option value="all">{hosted ? 'All team members' : 'All instructors'}</option>
          {instructors.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
        </select>
      </div>

      {/* legend — §8 hosted mode uses the shared activity/payment vocabulary (one legend, no
          contradiction with the workspace); legacy standalone keeps its payment legend. */}
      {hosted ? (
        <div style={{ marginBottom: 10 }}>
          <ActivityPaymentLegend />
          <span style={{ fontSize: 10.5, color: C.t3 }}>each cell = A / P / N · colour = activity (client lessons show payment tint)</span>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 10, fontSize: 11, color: C.t2 }}>
          {Object.values(PAYMENT_STATES).map((p) => <span key={p.label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: p.color }} />{p.short}</span>)}
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: SCHEDULE_ENTRY_TYPES.training.color }} />Internal event</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: C.free, border: `1px solid ${C.border}` }} />Free</span>
          <span style={{ color: C.t3 }}>· each cell = A / P / N</span>
        </div>
      )}

      <div style={{ overflowX: 'auto', border: `1px solid ${C.border}`, borderRadius: 10 }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, position: 'sticky', left: 0, background: C.card2, zIndex: 2, minWidth: 150, textAlign: 'left' }}>{hosted ? 'Staff / Instructor' : 'Instructor'}</th>
              {dates.map((d, i) => (
                <th key={i} style={{ ...thStyle, minWidth: 58 }}>
                  <div style={{ fontSize: 9, color: C.t3 }}>{(i === 0 || (zonedDateParts(d, tz)?.day === 1)) ? fmtMonth(d) : ''}</div>
                  {fmtCol(d)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <React.Fragment key={g.key}>
                <tr><td colSpan={dates.length + 1} style={{ background: '#EFEDE8', fontWeight: 800, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: C.t2, padding: '5px 10px' }}>{g.label} · {g.list.length}</td></tr>
                {g.list.map((inst) => (
                  <tr key={inst.id}>
                    <td style={{ ...tdStyle, position: 'sticky', left: 0, background: C.card, zIndex: 1, textAlign: 'left' }}>
                      <div style={{ fontWeight: 700, color: C.t1 }}>{inst.name}</div>
                      <div style={{ fontSize: 10, color: C.t3 }}>{capBadge(inst)}</div>
                    </td>
                    {dates.map((d, di) => {
                      const cell = index[inst.id]?.[dKey(d)] || { am: [], pm: [], night: [], untimed: [] };
                      return (
                        <td key={di} style={tdStyle}>
                          <div style={{ display: 'flex', gap: 2 }}>
                            <Slot list={cell.am} label="AM" />
                            <Slot list={cell.pm} label="PM" />
                            <Slot list={cell.night} label="Night" />
                          </div>
                          {cell.untimed && cell.untimed.length > 0 && (
                            <div title={`${cell.untimed.length} record(s) with no recorded time`} style={{ marginTop: 2, fontSize: 8, color: C.t3, fontStyle: 'italic', whiteSpace: 'nowrap' }}>
                              ⧗ time n/a ({cell.untimed.length})
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
            ))}
            {index.__unassigned__ && (
              <>
                <tr><td colSpan={dates.length + 1} style={{ background: 'rgba(217,119,6,0.10)', fontWeight: 800, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: '#D97706', padding: '5px 10px' }}>Unassigned lessons</td></tr>
                <tr>
                  <td style={{ ...tdStyle, position: 'sticky', left: 0, background: C.card, zIndex: 1, textAlign: 'left', fontWeight: 700 }}>Unassigned</td>
                  {dates.map((d, di) => {
                    const cell = index.__unassigned__?.[dKey(d)] || { am: [], pm: [], night: [] };
                    return (<td key={di} style={tdStyle}><div style={{ display: 'flex', gap: 2 }}><Slot list={cell.am} label="AM" /><Slot list={cell.pm} label="PM" /><Slot list={cell.night} label="Night" /></div></td>);
                  })}
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
      {groups.length === 0 && <div style={{ color: C.t3, fontSize: 14, padding: 16 }}>No instructors match the current filters.</div>}
    </div>
  );
}

const thStyle = { padding: '6px 4px', borderBottom: `1px solid ${C.border}`, fontSize: 10, fontWeight: 700, color: C.t2, textAlign: 'center' };
const tdStyle = { padding: '4px', borderBottom: `1px solid ${C.border}`, textAlign: 'center', verticalAlign: 'middle' };
function inp(w) { return { minWidth: w, boxSizing: 'border-box', background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '6px 9px', fontSize: 12, fontFamily: font, color: C.t1, marginLeft: 4 }; }
